import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import Select from './Select';
import { formatDateForDB } from '../../utils/dateUtils';

const BookingModal = ({ 
  isOpen = false, 
  onClose, 
  onSave,
  onDelete,
  booking = null,
  selectedDate = null,
  selectedTime = null,
  customers = [],
  services = []
}) => {
  const [formData, setFormData] = useState({
    customerId: '',
    date: '',
    time: '',
    duration: '30',
    serviceId: '',
    service: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (booking) {
        setFormData({
          customerId: booking?.customer_id || '',
          date: booking?.date || '',
          time: booking?.time || '',
          duration: booking?.duration?.toString() || '30',
          serviceId: booking?.service_id || '',
          service: booking?.service || '',
          notes: booking?.notes || ''
        });
      } else {
        setFormData({
          customerId: '',
          date: selectedDate || formatDateForDB(new Date()),
          time: selectedTime || '09:00',
          duration: '30',
          serviceId: '',
          service: '',
          notes: ''
        });
      }
      setErrors({});
    }
  }, [isOpen, booking, selectedDate, selectedTime]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget) {
      handleClose();
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.customerId) {
      newErrors.customerId = 'Please select a customer';
    }
    if (!formData?.date) {
      newErrors.date = 'Date is required';
    }
    if (!formData?.time) {
      newErrors.time = 'Time is required';
    }
    if (!formData?.serviceId) {
      newErrors.service = 'Service is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const selectedCustomer = customers?.find(c => c?.id === formData?.customerId);
      await onSave({
        ...formData,
        id: booking?.id || Date.now()?.toString(),
        customerName: selectedCustomer?.name || '',
        customerEmail: selectedCustomer?.email || '',
        customerPhone: selectedCustomer?.phone || '',
        createdAt: booking?.createdAt || new Date()?.toISOString(),
        updatedAt: new Date()?.toISOString()
      });
      handleClose();
    } catch (error) {
      setErrors({ submit: 'Failed to save booking. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      await onDelete(booking?.id);
      setShowDeleteConfirm(false);
      handleClose();
    } catch (error) {
      setErrors({ submit: 'Failed to delete booking. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    // When service is selected, store both ID and name
    if (field === 'serviceId') {
      const selectedService = services?.find(s => s?.id === value);
      setFormData(prev => ({ 
        ...prev, 
        serviceId: value,
        service: selectedService?.name || ''
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const customerOptions = customers?.map(customer => ({
    value: customer?.id,
    label: `${customer?.name} - ${customer?.phone}`,
    description: customer?.email
  }));

  const durationOptions = [
    { value: '60', label: '1 hour' },
    { value: '90', label: '1.5 hours' },
    { value: '120', label: '2 hours' },
    { value: '150', label: '2.5 hours' },
    { value: '180', label: '3 hours' },
    { value: '210', label: '3.5 hours' },
    { value: '240', label: '4 hours' },
    { value: '270', label: '4.5 hours' },
    { value: '300', label: '5 hours' },
    { value: '330', label: '5.5 hours' },
    { value: '360', label: '6 hours' }
  ];

  const serviceOptions = services?.map(service => ({
    value: service?.id,
    label: service?.name
  }));

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed inset-0 z-modal flex items-start justify-center pt-[8vh] px-4"
      onClick={handleBackdropClick}
    >
      <div className="fixed inset-0 bg-background" />
      
      <div className="relative w-full max-w-2xl bg-card rounded-xl shadow-elevation-5 overflow-hidden">
        <div className="flex items-center justify-between px-8 py-6 border-b border-border">
          <h2 className="text-h3 font-heading font-semibold text-foreground">
            {booking ? 'Edit Appointment' : 'New Appointment'}
          </h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth disabled:opacity-50"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[calc(100vh-24vh)] overflow-y-auto">
          <Select
            label="Customer"
            description="Select the customer for this appointment"
            options={customerOptions}
            value={formData?.customerId}
            onChange={(value) => handleChange('customerId', value)}
            error={errors?.customerId}
            required
            searchable
            placeholder="Search customers..."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Date"
              type="date"
              value={formData?.date}
              onChange={(e) => handleChange('date', e?.target?.value)}
              error={errors?.date}
              required
            />

            <Input
              label="Time"
              type="time"
              value={formData?.time}
              onChange={(e) => handleChange('time', e?.target?.value)}
              error={errors?.time}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Select
              label="Duration"
              options={durationOptions}
              value={formData?.duration}
              onChange={(value) => handleChange('duration', value)}
              required
            />

            <Select
              label="Service"
              options={serviceOptions}
              value={formData?.serviceId}
              onChange={(value) => handleChange('serviceId', value)}
              error={errors?.service}
              required
              placeholder="Select service type..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Notes
            </label>
            <textarea
              value={formData?.notes}
              onChange={(e) => handleChange('notes', e?.target?.value)}
              placeholder="Add any additional notes or special requirements..."
              rows={4}
              className="w-full px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background transition-smooth resize-none"
            />
          </div>

          {errors?.submit && (
            <div className="p-4 bg-error/10 border border-error rounded-lg">
              <p className="text-sm text-error">{errors?.submit}</p>
            </div>
          )}

          {showDeleteConfirm && (
            <div className="p-4 bg-warning/10 border border-warning rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground mb-1">Confirm Deletion</p>
                  <p className="text-sm text-muted-foreground mb-3">
                    Are you sure you want to delete this booking? This action cannot be undone.
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={handleDelete}
                      loading={isSubmitting}
                      iconName="Trash2"
                      iconPosition="left"
                    >
                      Delete Booking
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 pt-4 border-t border-border">
            <div>
              {booking && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isSubmitting || showDeleteConfirm}
                  iconName="Trash2"
                  iconPosition="left"
                >
                  Delete
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                loading={isSubmitting}
                iconName="Check"
                iconPosition="left"
              >
                {booking ? 'Update Appointment' : 'Create Appointment'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default BookingModal;